import { ShowDocumentDetailsEnum } from "@/enums";
import { useGomakeAxios, useSnackBar } from "@/hooks";
import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import { updateDocumentItemContentApi } from "@/services/api-service/generic-doc/documents-api";
import { quoteItemState } from "@/store";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useSetRecoilState } from "recoil";
import { hasTableCellUpdatesState } from "@/pages-components/quote-new/store/quote";
import { splitAtPrintingDetails } from "./split-item-details-summary";
import { getDocumentItemDetailsByDefaultLanguage } from "../../utils/document-item-translations";

export type ItemDetailsSummaryCollapseMode = "default" | "beforePrintingDetails";

const useCharacterDetails = ({
  details,
  getQuote,
  documentItemId,
  documentType,
  workName,
  defaultLanguage,
  summaryCollapseMode = "default" as ItemDetailsSummaryCollapseMode,
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const dir = t("direction");
  const isRTL = dir === "rtl";
  const { callApi } = useGomakeAxios();
  const { alertSuccessUpdate, alertFaultUpdate } = useSnackBar();
  const [showAll, setShowAll] = useState(false);
  const [originalValue, setOriginalValue] = useState(details);
  const [data, setData] = useState(details);
  useEffect(() => {
    setData(details);
    setOriginalValue(details);
  }, [details]);
  useEffect(() => {
    setShowAll(false);
  }, [details]);
  const [jobName, setJobName] = useState(workName);
  useEffect(() => {
    setJobName(workName);
  }, []);
  const [isValueChanged, setIsValueChanged] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [quoteItemValue, setQuoteItemValue] =
    useRecoilState<any>(quoteItemState);
  const structuredSplit = useMemo(() => {
    if (summaryCollapseMode !== "beforePrintingDetails" || !details) {
      return null;
    }
    return splitAtPrintingDetails(String(details));
  }, [details, summaryCollapseMode]);

  const truncatedDetails = useMemo(() => {
    if (!details) {
      return "";
    }
    const s = String(details);
    if (structuredSplit?.hasMore) {
      return showAll ? s : structuredSplit.summary;
    }
    return showAll ? s : s.slice(0, 90);
  }, [details, showAll, structuredSplit]);

  const showExpandToggle = structuredSplit?.hasMore
    ? true
    : !!(details && String(details).length > 90);

  const showTruncationEllipsis =
    !showAll &&
    !structuredSplit?.hasMore &&
    !!(details && String(details).length > 90);
  const setHasTableCellUpdates = useSetRecoilState(hasTableCellUpdatesState);
  const canNotEditWhenShowJobNameOnly =
    quoteItemValue?.showDetails === ShowDocumentDetailsEnum.ShowJobNameOnly;

  const handleShowMore = () => {
    setShowAll(true);
  };

  const handleShowLess = () => {
    setShowAll(false);
  };
  const formatContent = (str: string) => {
    // First replace closing </p> tags with <br/>
    let formatted = str?.replace(/<\/p>/g, "<br/>");
    // Then remove remaining <p> tags
    formatted = formatted?.replace(/<p>/g, "");
    // Keep <strong> tags and <br/> tags, remove any other HTML tags
    formatted = formatted?.replace(/<(?!\/?strong|\/?br\/?)[^>]+>/g, "");
    return formatted;
  };

  const updateDocumentItemsContent = (
    items: any[] = [],
    nextDetails: string,
    translations?: Array<{ lang: string; name: string }>
  ) => {
    let didUpdate = false;

    const updatedItems = items.map((item) => {
      if (String(item?.id) === String(documentItemId)) {
        didUpdate = true;
        return {
          ...item,
          details: nextDetails,
          ...(translations && { contentTranslations: translations }),
        };
      }

      if (Array.isArray(item?.childsDocumentItems)) {
        const childResult = updateDocumentItemsContent(
          item.childsDocumentItems,
          nextDetails,
          translations
        );

        if (childResult.didUpdate) {
          didUpdate = true;
          return {
            ...item,
            childsDocumentItems: childResult.items,
          };
        }
      }

      return item;
    });

    return { items: updatedItems, didUpdate };
  };

  const updateDocumentItemContent = async (
    jobName?: string,
    data?: string,
    translations?: Array<{ lang: string; name: string }>
  ) => {
    if (router.query.isNewCreation) {
      const formattedData = formatContent(data);
      const formattedTranslations = translations?.map((trans) => ({
        lang: trans.lang,
        name: formatContent(trans.name),
      }));
      const defaultLanguageDetails = getDocumentItemDetailsByDefaultLanguage(
        {
          details: formattedData,
          contentTranslations: formattedTranslations,
        },
        defaultLanguage ?? quoteItemValue?.defaultLanguage,
      );
      const updatedQuoteItemValue = { ...quoteItemValue };
      const updateResult = updateDocumentItemsContent(
        updatedQuoteItemValue.documentItems,
        defaultLanguageDetails,
        formattedTranslations
      );

      if (updateResult.didUpdate) {
        setQuoteItemValue({
          ...updatedQuoteItemValue,
          documentItems: updateResult.items,
        });
      }
    } else {
      const callBack = (res) => {
        if (res?.success) {
          alertSuccessUpdate();

          //   Mark that table cells (details) were updated for orders
          if (documentType === DOCUMENT_TYPE.order && !router.query.isNewCreation) {
            setHasTableCellUpdates(true);
          }
          if (getQuote) {
            getQuote();
          }
        } else {
          alertFaultUpdate();
        }
      };
      await updateDocumentItemContentApi(callApi, callBack, {
        documentType: documentType ?? DOCUMENT_TYPE.quote,
        contact: {
          documentItemId: documentItemId,
          content: formatContent(data),
          jobName: jobName,
          ...(translations && {
            contentTranslations: translations.map((trans) => ({
              lang: trans.lang,
              name: formatContent(trans.name),
            })),
          }),
        },
      });
    }
  };

  const handleClose = () => {
    if (isValueChanged) {
      setData(originalValue);
    }
    setIsEdit(false);
  };

  const handleSave = (
    data: string,
    translations?: Array<{ lang: string; name: string }>
  ) => {
    const hasDataChanged = data !== originalValue;
    const hasJobNameChanged = jobName !== workName;

    // Early return if nothing has changed
    if (!hasDataChanged && !hasJobNameChanged && !translations) {
      setIsEdit(false);
      return;
    }

    // Only update if something has actually changed
    if (hasJobNameChanged) {
      updateDocumentItemContent(jobName, data, translations);
    } else if (hasDataChanged || translations) {
      updateDocumentItemContent(null, data, translations);
    }

    // Update the original value only after successful change
    setOriginalValue(data);
    setIsEdit(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setIsValueChanged(data !== originalValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [data, originalValue]);

  return {
    t,
    isRTL,
    isEdit,
    showAll,
    truncatedDetails,
    data,
    handleShowLess,
    handleShowMore,
    setIsEdit,
    setData,
    handleClose,
    handleSave,
    jobName,
    setJobName,
    canNotEditWhenShowJobNameOnly,
    showExpandToggle,
    showTruncationEllipsis,
  };
};

export { useCharacterDetails };
